import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  Activity,
  AlertTriangle,
  CircleOff,
  Filter,
  Link2,
  Pause,
  Play,
  RefreshCw,
  Search,
  Timer,
  Waves,
} from "lucide-react";
import { fetchLogsPage, fetchRecentLogs, fetchTraceBundle, fetchTracesPage } from "@/lib/api";
import { connectLiveTopic } from "@/lib/ws";
import {
  isErrorEvent,
  logToUnifiedEvent,
  readRuntimeConfig,
  sortEventsAsc,
  sortEventsDesc,
  spanToUnifiedEvent,
} from "@/lib/telemetry";
import {
  buildAccountGithubLoginUrl,
  canAttemptAuthRedirectNow,
  resolveAccountOrigin,
  shouldAutoRedirectToLogin,
} from "@/lib/auth";
import { buildEventSecondaryLine, compactSql, extractEventDetails } from "@/lib/eventDetails";
import type { SessionAggregate, UnifiedEvent } from "@/lib/types";
import { formatDurationMs, formatUtcDateTime, hashToHue, shortId } from "@/lib/utils";
import {
  useTelemetryStore,
  type SeverityFilter,
  type ViewMode,
} from "@/store/useTelemetryStore";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Select } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, Td, Th } from "@/components/ui/table";

const PAGE_SIZE = 100;
const RECENT_SECONDS = 60 * 60 * 6;
const RECENT_LIMIT = 1_500;

const severityWeight = (severity: string | null, numberValue: number | null): number => {
  const text = (severity ?? "").toLowerCase();
  if (text === "fatal") return 5;
  if (text === "error") return 4;
  if (text === "warn" || text === "warning") return 3;
  if (text === "info") return 2;
  if (text === "debug" || text === "trace") return 1;
  if (typeof numberValue === "number") {
    if (numberValue >= 21) return 5;
    if (numberValue >= 17) return 4;
    if (numberValue >= 13) return 3;
    if (numberValue >= 9) return 2;
    if (numberValue >= 5) return 1;
  }
  return 0;
};

const passesSeverity = (event: UnifiedEvent, filter: SeverityFilter): boolean => {
  if (filter === "all") return true;
  if (event.kind === "span") {
    if (filter === "error") return event.statusCode === 2;
    return false;
  }
  const current = severityWeight(event.severityText, event.severityNumber);
  const target =
    filter === "debug" ? 1 : filter === "info" ? 2 : filter === "warn" ? 3 : filter === "error" ? 4 : 0;
  return current >= target;
};

const normalizeFilterText = (value: string): string => value.trim().toLowerCase();

const matchesFilters = (
  event: UnifiedEvent,
  filters: {
    search: string;
    service: string;
    sessionId: string;
    sessionEventId: string;
    traceId: string;
    severity: SeverityFilter;
    errorOnly: boolean;
  },
): boolean => {
  if (filters.errorOnly && !isErrorEvent(event)) return false;
  if (!passesSeverity(event, filters.severity)) return false;

  const serviceFilter = normalizeFilterText(filters.service);
  if (serviceFilter && !(event.serviceName ?? "").toLowerCase().includes(serviceFilter)) return false;

  const sessionFilter = normalizeFilterText(filters.sessionId);
  if (sessionFilter && !(event.sessionId ?? "").toLowerCase().includes(sessionFilter)) return false;

  const sessionEventFilter = normalizeFilterText(filters.sessionEventId);
  if (sessionEventFilter && !(event.sessionEventId ?? "").toLowerCase().includes(sessionEventFilter)) return false;

  const traceFilter = normalizeFilterText(filters.traceId);
  if (traceFilter && !(event.traceId ?? "").toLowerCase().includes(traceFilter)) return false;

  const search = normalizeFilterText(filters.search);
  if (!search) return true;
  const haystack = [
    event.message,
    event.serviceName ?? "",
    event.traceId ?? "",
    event.spanId ?? "",
    event.sessionId ?? "",
    event.sessionEventId ?? "",
    event.userId ?? "",
    JSON.stringify(event.attributes),
  ]
    .join(" ")
    .toLowerCase();

  return haystack.includes(search);
};

type ComponentTiming = {
  key: string;
  serviceName: string;
  spanName: string;
  count: number;
  errors: number;
  totalMs: number;
  avgMs: number;
  maxMs: number;
};

const buildComponentTiming = (events: UnifiedEvent[]): ComponentTiming[] => {
  const map = new Map<string, ComponentTiming>();
  for (const event of events) {
    if (event.kind !== "span") continue;
    const serviceName = event.serviceName ?? "unknown-service";
    const spanName = event.message || "span";
    const key = `${serviceName}::${spanName}`;
    const current = map.get(key) ?? {
      key,
      serviceName,
      spanName,
      count: 0,
      errors: 0,
      totalMs: 0,
      avgMs: 0,
      maxMs: 0,
    };
    current.count += 1;
    if (event.statusCode === 2) current.errors += 1;
    if (typeof event.durationMs === "number") {
      current.totalMs += event.durationMs;
      current.maxMs = Math.max(current.maxMs, event.durationMs);
    }
    map.set(key, current);
  }
  return [...map.values()]
    .map((item) => ({
      ...item,
      avgMs: item.count > 0 ? item.totalMs / item.count : 0,
    }))
    .sort((a, b) => b.totalMs - a.totalMs || b.errors - a.errors)
    .slice(0, 50);
};

const buildSessions = (events: UnifiedEvent[]): SessionAggregate[] => {
  const map = new Map<string, SessionAggregate>();
  for (const event of events) {
    if (!event.sessionId) continue;
    const current =
      map.get(event.sessionId) ?? {
        sessionId: event.sessionId,
        firstTsMs: event.tsMs,
        lastTsMs: event.tsMs,
        totalEvents: 0,
        errorEvents: 0,
        traces: new Set<string>(),
        services: new Set<string>(),
        users: new Set<string>(),
      };

    current.firstTsMs = Math.min(current.firstTsMs, event.tsMs);
    current.lastTsMs = Math.max(current.lastTsMs, event.tsMs);
    current.totalEvents += 1;
    if (isErrorEvent(event)) current.errorEvents += 1;
    if (event.traceId) current.traces.add(event.traceId);
    if (event.serviceName) current.services.add(event.serviceName);
    if (event.userId) current.users.add(event.userId);
    map.set(event.sessionId, current);
  }

  return [...map.values()].sort((a, b) => b.lastTsMs - a.lastTsMs);
};

const getSessionColor = (sessionId: string): string => {
  const hue = hashToHue(sessionId);
  return `hsl(${hue} 90% 57%)`;
};

const compactTime = (tsMs: number): string => {
  const d = new Date(tsMs);
  const hh = String(d.getUTCHours()).padStart(2, "0");
  const mm = String(d.getUTCMinutes()).padStart(2, "0");
  const ss = String(d.getUTCSeconds()).padStart(2, "0");
  const ms = String(d.getUTCMilliseconds()).padStart(3, "0");
  return `${hh}:${mm}:${ss}.${ms}`;
};

const prettyJson = (value: unknown): string => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

export default function App() {
  const config = useMemo(() => readRuntimeConfig(), []);
  const [wsLogsStatus, setWsLogsStatus] = useState("idle");
  const [wsTracesStatus, setWsTracesStatus] = useState("idle");
  const [wsError, setWsError] = useState<string | null>(null);
  const [authRedirecting, setAuthRedirecting] = useState(false);

  const view = useTelemetryStore((state) => state.view);
  const density = useTelemetryStore((state) => state.density);
  const paused = useTelemetryStore((state) => state.paused);
  const filters = useTelemetryStore((state) => state.filters);
  const selectedTraceId = useTelemetryStore((state) => state.selectedTraceId);
  const selectedSessionId = useTelemetryStore((state) => state.selectedSessionId);
  const selectedEvent = useTelemetryStore((state) => state.selectedEvent);
  const liveEvents = useTelemetryStore((state) => state.liveEvents);
  const backlogEvents = useTelemetryStore((state) => state.backlogEvents);

  const setView = useTelemetryStore((state) => state.setView);
  const setDensity = useTelemetryStore((state) => state.setDensity);
  const setPaused = useTelemetryStore((state) => state.setPaused);
  const setFilters = useTelemetryStore((state) => state.setFilters);
  const clearFilters = useTelemetryStore((state) => state.clearFilters);
  const setSelectedTraceId = useTelemetryStore((state) => state.setSelectedTraceId);
  const setSelectedSessionId = useTelemetryStore((state) => state.setSelectedSessionId);
  const setSelectedEvent = useTelemetryStore((state) => state.setSelectedEvent);
  const addIncomingEvents = useTelemetryStore((state) => state.addIncomingEvents);
  const clearLiveEvents = useTelemetryStore((state) => state.clearLiveEvents);
  const catchUp = useTelemetryStore((state) => state.catchUp);

  const logsQuery = useQuery({
    queryKey: ["logs", config.org, config.telemetryOrigin, config.authToken],
    enabled: Boolean(config.org),
    queryFn: () => fetchLogsPage(config, 1, PAGE_SIZE),
    refetchInterval: paused ? false : 15_000,
  });

  const tracesQuery = useQuery({
    queryKey: ["traces", config.org, config.telemetryOrigin, config.authToken],
    enabled: Boolean(config.org),
    queryFn: () => fetchTracesPage(config, 1, PAGE_SIZE),
    refetchInterval: paused ? false : 15_000,
  });

  const recentLogsQuery = useQuery({
    queryKey: ["recent-logs", config.org, config.telemetryOrigin, config.authToken],
    enabled: Boolean(config.org),
    queryFn: () => fetchRecentLogs(config, RECENT_SECONDS, RECENT_LIMIT),
    refetchInterval: paused ? false : 20_000,
  });

  const traceBundleQuery = useQuery({
    queryKey: ["trace", config.org, config.telemetryOrigin, config.authToken, selectedTraceId],
    enabled: Boolean(config.org && selectedTraceId),
    queryFn: () => fetchTraceBundle(config, selectedTraceId!),
  });

  useEffect(() => {
    if (!config.org || authRedirecting) return;
    const errors = [logsQuery.error, tracesQuery.error, recentLogsQuery.error, traceBundleQuery.error];
    if (!shouldAutoRedirectToLogin(config, errors)) return;
    if (!canAttemptAuthRedirectNow()) return;
    setAuthRedirecting(true);
    const accountOrigin = resolveAccountOrigin(config.telemetryOrigin);
    window.location.href = buildAccountGithubLoginUrl(accountOrigin, window.location.href);
  }, [
    authRedirecting,
    config,
    logsQuery.error,
    tracesQuery.error,
    recentLogsQuery.error,
    traceBundleQuery.error,
  ]);

  useEffect(() => {
    if (!config.org) return;

    const logsSocket = connectLiveTopic(
      {
        telemetryOrigin: config.telemetryOrigin,
        org: config.org,
        topic: "logs",
        authToken: config.authToken,
      },
      {
        onEvents: (events) => {
          addIncomingEvents(events);
        },
        onStatus: setWsLogsStatus,
        onError: (message) => {
          setWsError(message);
          setWsLogsStatus("error");
        },
      },
    );

    const tracesSocket = connectLiveTopic(
      {
        telemetryOrigin: config.telemetryOrigin,
        org: config.org,
        topic: "traces",
        authToken: config.authToken,
      },
      {
        onEvents: (events) => {
          addIncomingEvents(events);
        },
        onStatus: setWsTracesStatus,
        onError: (message) => {
          setWsError(message);
          setWsTracesStatus("error");
        },
      },
    );

    return () => {
      logsSocket.close();
      tracesSocket.close();
    };
  }, [addIncomingEvents, config.authToken, config.org, config.telemetryOrigin]);

  const historicalEvents = useMemo(() => {
    const logs = (recentLogsQuery.data?.items ?? logsQuery.data?.items ?? []).map(logToUnifiedEvent);
    const traces = (tracesQuery.data?.items ?? []).map(spanToUnifiedEvent);
    return sortEventsDesc([...logs, ...traces]);
  }, [logsQuery.data?.items, recentLogsQuery.data?.items, tracesQuery.data?.items]);

  const mergedEvents = useMemo(() => {
    const map = new Map<string, UnifiedEvent>();
    for (const event of historicalEvents) map.set(event.key, event);
    for (const event of liveEvents) map.set(event.key, event);
    return sortEventsDesc([...map.values()]);
  }, [historicalEvents, liveEvents]);

  const filteredEvents = useMemo(() => {
    return mergedEvents.filter((event) => matchesFilters(event, filters));
  }, [filters, mergedEvents]);

  const traceBundleEvents = useMemo(() => {
    if (!traceBundleQuery.data) return [];
    const spans = traceBundleQuery.data.spans.map(spanToUnifiedEvent);
    const logs = traceBundleQuery.data.logs.map(logToUnifiedEvent);
    return sortEventsAsc([...spans, ...logs]);
  }, [traceBundleQuery.data]);

  const tracePanelEvents = useMemo(() => {
    if (!selectedTraceId) return [];
    if (traceBundleEvents.length > 0) return traceBundleEvents;
    return sortEventsAsc(filteredEvents.filter((event) => event.traceId === selectedTraceId));
  }, [filteredEvents, selectedTraceId, traceBundleEvents]);

  const traceSpans = useMemo(
    () => tracePanelEvents.filter((event) => event.kind === "span"),
    [tracePanelEvents],
  );

  const traceRange = useMemo(() => {
    if (traceSpans.length === 0) {
      return { min: 0, max: 1, total: 1 };
    }
    const min = Math.min(...traceSpans.map((event) => event.tsMs));
    const max = Math.max(...traceSpans.map((event) => event.tsMs + (event.durationMs ?? 0)));
    return {
      min,
      max,
      total: Math.max(1, max - min),
    };
  }, [traceSpans]);

  const componentTiming = useMemo(() => {
    const source = selectedTraceId ? tracePanelEvents : filteredEvents;
    return buildComponentTiming(source);
  }, [filteredEvents, selectedTraceId, tracePanelEvents]);

  const sessionAggregates = useMemo(() => buildSessions(filteredEvents), [filteredEvents]);

  const services = useMemo(() => {
    return [...new Set(mergedEvents.map((event) => event.serviceName).filter(Boolean) as string[])].sort();
  }, [mergedEvents]);

  const selectedSessionTimeline = useMemo(() => {
    if (!selectedSessionId) return [];
    return sortEventsAsc(filteredEvents.filter((event) => event.sessionId === selectedSessionId));
  }, [filteredEvents, selectedSessionId]);

  const selectedEventPayload = useMemo(() => {
    if (!selectedEvent) return "No selection.";
    return prettyJson(selectedEvent.raw ?? selectedEvent);
  }, [selectedEvent]);
  const selectedEventDetails = useMemo(
    () => (selectedEvent ? extractEventDetails(selectedEvent) : null),
    [selectedEvent],
  );

  const totalErrorEvents = useMemo(() => filteredEvents.filter((event) => isErrorEvent(event)).length, [filteredEvents]);

  const liveStatusBadgeVariant = wsError
    ? "destructive"
    : wsLogsStatus.includes("connected") && wsTracesStatus.includes("connected")
      ? "default"
      : "secondary";

  const rowPaddingClass = density === "compact" ? "py-1" : "py-2";

  if (!config.org) {
    return (
      <div className="flex h-full w-full items-center justify-center p-6">
        <Card className="max-w-xl">
          <CardHeader>
            <CardTitle>Missing Org Scope</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm text-muted-foreground">
            <p>This UI requires an org in the path.</p>
            <p className="mono">Example: https://logs.w7s.io/omattic</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="flex h-full w-full flex-col overflow-hidden p-2">
      <Card className="mb-2 w-full">
        <CardHeader className="flex flex-col gap-2 p-2 md:flex-row md:items-center md:justify-between">
          <div className="flex items-center gap-2">
            <Waves className="size-4 text-primary" />
            <CardTitle>W7S Observability Workspace</CardTitle>
            <Badge variant="outline" className="mono text-[10px]">
              /{config.org}
            </Badge>
            <Badge variant={liveStatusBadgeVariant as "default" | "secondary" | "destructive"} className="mono text-[10px]">
              {wsError ? "stream degraded" : "stream active"}
            </Badge>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="secondary" className="mono text-[10px]">
              {filteredEvents.length} events
            </Badge>
            <Badge variant={totalErrorEvents > 0 ? "destructive" : "secondary"} className="mono text-[10px]">
              {totalErrorEvents} errors
            </Badge>
            <Badge variant="secondary" className="mono text-[10px]">
              {sessionAggregates.length} sessions
            </Badge>
            <Button
              size="sm"
              variant={paused ? "destructive" : "secondary"}
              onClick={() => setPaused(!paused)}
              title={paused ? "Resume live updates" : "Pause live updates"}
            >
              {paused ? <Play className="size-3" /> : <Pause className="size-3" />}
              {paused ? "Resume" : "Pause"}
            </Button>
            {paused && backlogEvents.length > 0 ? (
              <Button size="sm" variant="outline" onClick={catchUp}>
                Catch up ({backlogEvents.length})
              </Button>
            ) : null}
            <Button
              size="sm"
              variant="outline"
              onClick={() => {
                void logsQuery.refetch();
                void tracesQuery.refetch();
                void recentLogsQuery.refetch();
                if (selectedTraceId) void traceBundleQuery.refetch();
              }}
            >
              <RefreshCw className="size-3" />
              Refresh
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-2 px-2 pb-2">
          <div className="grid grid-cols-1 gap-2 md:grid-cols-8">
            <div className="relative md:col-span-2">
              <Search className="pointer-events-none absolute left-2 top-2 size-3 text-muted-foreground" />
              <Input
                className="pl-7"
                placeholder="Search logs, spans, attrs"
                value={filters.search}
                onChange={(event) => setFilters({ search: event.target.value })}
              />
            </div>
            <Input
              placeholder="Session"
              value={filters.sessionId}
              onChange={(event) => setFilters({ sessionId: event.target.value })}
            />
            <Input
              placeholder="Session Event"
              value={filters.sessionEventId}
              onChange={(event) => setFilters({ sessionEventId: event.target.value })}
            />
            <Input
              placeholder="Trace"
              value={filters.traceId}
              onChange={(event) => setFilters({ traceId: event.target.value })}
            />
            <Select value={filters.service} onChange={(event) => setFilters({ service: event.target.value })}>
              <option value="">All services</option>
              {services.map((service) => (
                <option key={service} value={service}>
                  {service}
                </option>
              ))}
            </Select>
            <Select
              value={filters.severity}
              onChange={(event) => setFilters({ severity: event.target.value as SeverityFilter })}
            >
              <option value="all">All severity</option>
              <option value="debug">Debug+</option>
              <option value="info">Info+</option>
              <option value="warn">Warn+</option>
              <option value="error">Error only</option>
            </Select>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant={filters.errorOnly ? "destructive" : "secondary"}
                onClick={() => setFilters({ errorOnly: !filters.errorOnly })}
                className="w-full"
              >
                <Filter className="size-3" />
                {filters.errorOnly ? "Errors only" : "All events"}
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Tabs value={view} onValueChange={(value) => setView(value as ViewMode)}>
              <TabsList>
                <TabsTrigger value="live">Live</TabsTrigger>
                <TabsTrigger value="trace">Trace</TabsTrigger>
                <TabsTrigger value="sessions">Sessions</TabsTrigger>
                <TabsTrigger value="explore">Explore</TabsTrigger>
              </TabsList>
            </Tabs>
            <Separator orientation="vertical" className="mx-1 h-6" />
            <Select value={density} onChange={(event) => setDensity(event.target.value as "compact" | "comfortable")}> 
              <option value="compact">Compact</option>
              <option value="comfortable">Comfortable</option>
            </Select>
            <Button size="sm" variant="outline" onClick={clearFilters}>
              <CircleOff className="size-3" />
              Reset Filters
            </Button>
            <Button size="sm" variant="outline" onClick={clearLiveEvents}>
              Clear Live Buffer
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-2 xl:grid-cols-[1.3fr_1.4fr_0.95fr]">
        <Card className="dense-scroll min-h-0 overflow-auto xl:resize-horizontal">
          <CardHeader className="p-2">
            <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <Activity className="size-3" />
              Unified Event Stream
            </CardTitle>
          </CardHeader>
          <CardContent className="px-0 pb-0">
            <Table>
              <thead className="sticky top-0 bg-card">
                <tr>
                  <Th className="mono">Time</Th>
                  <Th>Type</Th>
                  <Th>Service</Th>
                  <Th>Message</Th>
                  <Th className="mono">Trace</Th>
                  <Th className="mono">Session</Th>
                  <Th className="mono">Session Event</Th>
                </tr>
              </thead>
              <tbody>
                {filteredEvents.map((event) => {
                  const isSelected = selectedEvent?.key === event.key;
                  const sessionColor = event.sessionId ? getSessionColor(event.sessionId) : "transparent";
                  const detailLine = buildEventSecondaryLine(event);
                  return (
                    <tr
                      key={event.key}
                      className={`cursor-pointer transition-colors hover:bg-muted/40 ${
                        isSelected ? "bg-muted/60" : ""
                      }`}
                      onClick={() => {
                        setSelectedEvent(event);
                        if (event.traceId) setSelectedTraceId(event.traceId);
                        if (event.sessionId) setSelectedSessionId(event.sessionId);
                      }}
                    >
                      <Td className={`mono ${rowPaddingClass}`}>{compactTime(event.tsMs)}</Td>
                      <Td className={rowPaddingClass}>
                        {event.kind === "log" ? (
                          <Badge variant={isErrorEvent(event) ? "destructive" : "secondary"} className="mono text-[10px]">
                            {event.severityText?.toUpperCase() || "LOG"}
                          </Badge>
                        ) : (
                          <Badge variant={event.statusCode === 2 ? "destructive" : "outline"} className="mono text-[10px]">
                            {event.statusCode === 2 ? "SPAN ERR" : "SPAN"}
                          </Badge>
                        )}
                      </Td>
                      <Td className={`${rowPaddingClass} mono text-[11px]`}>{event.serviceName ?? "-"}</Td>
                      <Td className={rowPaddingClass}>
                        <div className="max-w-[45ch] truncate" title={event.message}>
                          {event.message}
                        </div>
                        {detailLine ? (
                          <div className="max-w-[45ch] truncate mono text-[10px] text-muted-foreground" title={detailLine}>
                            {detailLine}
                          </div>
                        ) : null}
                      </Td>
                      <Td className={`mono ${rowPaddingClass}`}>{shortId(event.traceId)}</Td>
                      <Td className={`mono ${rowPaddingClass}`}>
                        {event.sessionId ? (
                          <span className="inline-flex items-center gap-1">
                            <span className="inline-block size-2 rounded-full" style={{ backgroundColor: sessionColor }} />
                            {shortId(event.sessionId, 10)}
                          </span>
                        ) : (
                          "-"
                        )}
                      </Td>
                      <Td className={`mono ${rowPaddingClass}`}>{shortId(event.sessionEventId, 10)}</Td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </CardContent>
        </Card>

        <Card className="dense-scroll min-h-0 overflow-auto xl:resize-horizontal">
          <CardHeader className="p-2">
            <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              {view === "trace" ? <Link2 className="size-3" /> : view === "sessions" ? <Timer className="size-3" /> : <Activity className="size-3" />}
              {view === "trace" ? "Trace Correlation" : view === "sessions" ? "Session Correlation" : view === "explore" ? "Explore" : "Live Insights"}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {view === "trace" ? (
              selectedTraceId ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline" className="mono">Trace {selectedTraceId}</Badge>
                    {traceBundleQuery.isFetching ? <Badge variant="secondary">loading bundle…</Badge> : null}
                  </div>
                  <div className="space-y-1">
                    {traceSpans.map((span) => {
                      const startPct = ((span.tsMs - traceRange.min) / traceRange.total) * 100;
                      const widthPct = ((span.durationMs ?? 1) / traceRange.total) * 100;
                      const spanDetails = extractEventDetails(span);

                      return (
                        <div key={span.key} className="grid grid-cols-[220px_1fr_80px] items-center gap-2 text-xs">
                          <div className="min-w-0">
                            <div className="mono truncate" title={span.message}>
                              {span.message}
                            </div>
                            {spanDetails.dbStatement ? (
                              <div
                                className="mono truncate text-[10px] text-muted-foreground"
                                title={spanDetails.dbStatement}
                              >
                                SQL: {compactSql(spanDetails.dbStatement, 120)}
                              </div>
                            ) : null}
                          </div>
                          <div className="relative h-4 rounded bg-muted/50">
                            <div
                              className={`absolute inset-y-0 rounded ${span.statusCode === 2 ? "bg-destructive/80" : "bg-primary/80"}`}
                              style={{ left: `${Math.max(0, startPct)}%`, width: `${Math.max(1, widthPct)}%` }}
                            />
                          </div>
                          <div className="mono text-right">{formatDurationMs(span.durationMs)}</div>
                        </div>
                      );
                    })}
                  </div>
                  <Separator />
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Related Logs</h4>
                    {tracePanelEvents.filter((event) => event.kind === "log").slice(-25).map((log) => {
                      const details = extractEventDetails(log);
                      const detailLine = buildEventSecondaryLine(log);
                      return (
                        <div key={log.key} className="grid grid-cols-[100px_1fr] gap-2 text-xs">
                          <span className="mono text-muted-foreground">{compactTime(log.tsMs)}</span>
                          <div className="space-y-1">
                            <div>{log.message}</div>
                            {detailLine ? (
                              <div className="mono text-[10px] text-muted-foreground">{detailLine}</div>
                            ) : null}
                            {details.dbStatement ? (
                              <pre className="overflow-auto rounded border border-border/60 bg-background/70 p-2 mono text-[10px] leading-4">
                                {details.dbStatement}
                              </pre>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <p className="text-sm text-muted-foreground">Select a log/span row with a trace id to inspect full correlation.</p>
              )
            ) : null}

            {view === "sessions" ? (
              <div className="space-y-3">
                <div className="space-y-1">
                  {sessionAggregates.map((session) => (
                    <button
                      key={session.sessionId}
                      type="button"
                      className={`flex w-full items-center justify-between rounded border px-2 py-1 text-left text-xs transition-colors hover:bg-muted/50 ${
                        selectedSessionId === session.sessionId ? "bg-muted/60" : ""
                      }`}
                      onClick={() => setSelectedSessionId(session.sessionId)}
                    >
                      <span className="inline-flex items-center gap-2 mono">
                        <span className="inline-block size-2 rounded-full" style={{ backgroundColor: getSessionColor(session.sessionId) }} />
                        {session.sessionId}
                      </span>
                      <span className="mono text-muted-foreground">
                        {session.totalEvents} ev · {session.errorEvents} err
                      </span>
                    </button>
                  ))}
                </div>
                {selectedSessionId ? (
                  <div className="space-y-1">
                    <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Session Timeline</h4>
                    {selectedSessionTimeline.slice(-60).map((event) => (
                      <div key={event.key} className="grid grid-cols-[100px_1fr] gap-2 text-xs">
                        <span className="mono text-muted-foreground">{compactTime(event.tsMs)}</span>
                        <span className="truncate" title={event.message}>{event.message}</span>
                      </div>
                    ))}
                  </div>
                ) : null}
              </div>
            ) : null}

            {(view === "live" || view === "explore") ? (
              <div className="space-y-2">
                <h4 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Component Timing</h4>
                <Table>
                  <thead>
                    <tr>
                      <Th>Component</Th>
                      <Th className="mono">Count</Th>
                      <Th className="mono">Errors</Th>
                      <Th className="mono">Avg</Th>
                      <Th className="mono">Total</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {componentTiming.slice(0, 24).map((row) => (
                      <tr key={row.key}>
                        <Td className="mono text-[11px]">{row.serviceName}:{row.spanName}</Td>
                        <Td className="mono">{row.count}</Td>
                        <Td className="mono">{row.errors}</Td>
                        <Td className="mono">{formatDurationMs(row.avgMs)}</Td>
                        <Td className="mono">{formatDurationMs(row.totalMs)}</Td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            ) : null}
          </CardContent>
        </Card>

        <Card className="dense-scroll min-h-0 overflow-auto">
          <CardHeader className="p-2">
            <CardTitle className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
              <AlertTriangle className="size-3" />
              Inspector & Runtime Health
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="space-y-1 rounded border border-border/60 p-2 text-xs">
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">WS logs</span>
                <span className="mono">{wsLogsStatus}</span>
              </div>
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">WS traces</span>
                <span className="mono">{wsTracesStatus}</span>
              </div>
              {wsError ? <div className="mono text-destructive">{wsError}</div> : null}
              {authRedirecting ? (
                <div className="mono text-muted-foreground">
                  Authentication required. Redirecting to GitHub login…
                </div>
              ) : null}
              <div className="flex items-center justify-between">
                <span className="text-muted-foreground">Last refresh</span>
                <span className="mono">{compactTime(Date.now())}</span>
              </div>
            </div>

            <div className="space-y-1 rounded border border-border/60 p-2 text-xs">
              <div className="mono text-muted-foreground">Selected Event</div>
              {selectedEvent ? (
                <>
                  <div className="mono">Time: {formatUtcDateTime(selectedEvent.tsMs)}</div>
                  <div className="mono">Trace: {selectedEvent.traceId ?? "-"}</div>
                  <div className="mono">Session: {selectedEvent.sessionId ?? "-"}</div>
                  <div className="mono">Session Event: {selectedEvent.sessionEventId ?? "-"}</div>
                  <div className="mono">Duration: {formatDurationMs(selectedEvent.durationMs)}</div>
                  {selectedEventDetails?.pluginMethod ? (
                    <div className="mono">Method: {selectedEventDetails.pluginMethod}</div>
                  ) : null}
                  {selectedEventDetails?.dbStatement ? (
                    <div className="space-y-1">
                      <div className="mono">SQL:</div>
                      <pre className="overflow-auto rounded border border-border/60 bg-background/70 p-2 mono text-[10px] leading-4">
                        {selectedEventDetails.dbStatement}
                      </pre>
                    </div>
                  ) : null}
                </>
              ) : (
                <div className="text-muted-foreground">Click a row to inspect.</div>
              )}
            </div>

            <pre className="dense-scroll max-h-[52vh] overflow-auto rounded border border-border/60 bg-background/70 p-2 text-[11px] leading-5">
              {selectedEventPayload}
            </pre>

            {(logsQuery.error || tracesQuery.error || recentLogsQuery.error || traceBundleQuery.error) ? (
              <div className="rounded border border-destructive/30 bg-destructive/10 p-2 text-xs text-destructive">
                {(logsQuery.error as Error | undefined)?.message ||
                  (tracesQuery.error as Error | undefined)?.message ||
                  (recentLogsQuery.error as Error | undefined)?.message ||
                  (traceBundleQuery.error as Error | undefined)?.message}
              </div>
            ) : null}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
